import { SAVE_THE_DATE } from '../../constants/saveTheDate'

// The calendar lines and string lights are drawn as vector shapes inside
// that SVG (redrawn from Canva's raster graphics) so they stay sharp.
//
// The Save the Date card, designed in Canva (page 1 of "save the date"):
// everything except the photos is one SVG export, public/save-the-date-card.svg
// (text as shapes, so it matches Canva exactly). The photos are laid on top
// in the design's photo spots, each clipped to that spot's own rounded,
// tilted shape (public/save-the-date-frames/*.png, cut from the same export),
// so the animated clips keep moving.
//
// To change the design: edit it in Canva, delete the grey photo spots you
// fill here, export page 1 as SVG, and replace the file.

// Spot positions as a share of the card (from the 1080 x 1350 export).
const SPOTS = [
  // top right, above the calendar
  {
    frame: 'right',
    photo: 0,
    x: 75,
    y: 29.6,
    w: 23.2,
    h: 19.4,
    zoom: 0.86,
    focus: '50% 100%',
    // no paper behind the photo, so the string lights show around it
    clear: true,
  },
  // top left, above the calendar
  {
    frame: 'left',
    photo: 1,
    x: 3.82,
    y: 31.5,
    w: 23.14,
    h: 20.07,
    zoom: 0.9,
    focus: '45% 100%',
    // no paper behind the photo, so the string lights show around it
    clear: true,
  },
  // small spot, bottom left under the calendar (moved from the top right)
  {
    frame: 'small',
    photo: 3,
    // kept below the calendar's bottom line
    x: 4.6,
    y: 83.4,
    w: 16.6,
    h: 13.2,
    zoom: 1,
    focus: '50% 100%',
    // show the whole clip (chin and neck too) instead of cropping to fill
    contain: true,
    // turn the photo (degrees, + is clockwise)
    turn: 0,
    // mirrored, so it tilts the other way from the original spot
    flip: true,
  },
  // bottom right, under the calendar's bottom line
  {
    frame: 'bottom',
    photo: 2,
    x: 77.4,
    y: 80,
    w: 18.8,
    h: 16.6,
    zoom: 0.85,
    focus: '50% 100%',
  },
]

// Bulb centres on the string lights (1080 x 1350 export).
const BULBS = [
  [1052, 327],
  [966, 392],
  [38, 425],
  [863, 444],
  [160, 476],
  [754, 478],
  [638, 508],
  [297, 514],
  [530, 528],
  [396, 543],
  [146, 545],
  [272, 546],
  [441, 546],
  [588, 568],
  [1018, 578],
  [734, 580],
  [878, 584],
]

// Which bulbs gleam, in order, and when (seconds into the cycle). Uneven
// gaps of 3.5 to 6 s, so it never feels like a metronome.
const GLEAM_ORDER = [8, 2, 13, 5, 16, 0, 10, 4, 14, 7, 12]
const GLEAM_AT = [0, 4.2, 7.9, 13.4, 17.6, 22.9, 27.1, 31.0, 36.4, 40.3, 45.5]
const GLEAM_CYCLE = 50

// Fetch everything the card shows ahead of time (called while the guest is
// still playing), so the photos are ready when the envelope opens instead of
// popping in on a slow connection. Safe to call more than once.
let preloaded = false
export const preloadSaveTheDate = () => {
  if (preloaded) return
  preloaded = true
  const urls = [
    '/save-the-date-card.svg',
    '/save-the-date-frames/border.svg',
    '/save-the-date-frames/saturday.svg',
    '/save-the-date-frames/invitation.svg',
    '/save-the-date-frames/june12.svg',
    ...SPOTS.map((s) => `/save-the-date-frames/${s.frame}.png`),
    ...SAVE_THE_DATE.photos.flatMap((p) => (p ? [p.src] : [])),
  ]
  urls.forEach((src) => {
    const img = new Image()
    img.decoding = 'async'
    img.src = src
  })
}

export const CardContent = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0">
    <img
      src="/save-the-date-card.svg"
      alt=""
      draggable={false}
      className="absolute inset-0 block h-full w-full select-none"
    />
    {/* String lights. Every bulb glows warm all the time with a faint,
        never-repeating waver (two slow rhythms multiplied, like a filament
        on a dimmer). Now and then a single bulb's glass catches the light:
        a small highlight brightens and fades. Only one at a time, every few
        seconds, in a shuffled order. Under the photos. */}
    <svg
      viewBox="0 0 1080 1350"
      preserveAspectRatio="none"
      className="absolute inset-0 h-full w-full"
    >
      <defs>
        <radialGradient id="bulb-glow">
          <stop offset="0" stopColor="#FFE27A" stopOpacity="0.8" />
          <stop offset="0.4" stopColor="#FFD54F" stopOpacity="0.35" />
          <stop offset="1" stopColor="#FFD54F" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="bulb-flare">
          <stop offset="0" stopColor="#FFE27A" stopOpacity="0.75" />
          <stop offset="0.5" stopColor="#FFD54F" stopOpacity="0.25" />
          <stop offset="1" stopColor="#FFD54F" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="bulb-lit">
          <stop offset="0" stopColor="#FFFBEA" stopOpacity="0.95" />
          <stop offset="0.7" stopColor="#FFF3C2" stopOpacity="0.6" />
          <stop offset="1" stopColor="#FFF3C2" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="bulb-gleam">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.6" stopColor="#FFFBEA" stopOpacity="0.7" />
          <stop offset="1" stopColor="#FFF4C8" stopOpacity="0" />
        </radialGradient>
      </defs>
      {BULBS.map(([x, y], i) => {
        const slot = GLEAM_ORDER.indexOf(i)
        return (
          <g key={i} transform={`translate(${x} ${y})`}>
            <g
              className="std-waver"
              style={{
                animationDuration: `${3.7 + ((i * 7) % 5) * 0.61}s`,
                animationDelay: `${-((i * 13) % 17) * 0.37}s`,
              }}
            >
              <circle
                className="std-waver"
                cy="4"
                r="28"
                fill="url(#bulb-glow)"
                style={{
                  animationDuration: `${2.3 + ((i * 5) % 7) * 0.29}s`,
                  animationDelay: `${-((i * 11) % 13) * 0.23}s`,
                }}
              />
            </g>
            {slot >= 0 && (
              // The bulb catches the light: its glass brightens, a small warm
              // bloom rises around it, and a white highlight shows on the
              // upper left of the glass. Then it all settles back.
              <g
                className="std-gleam"
                style={{
                  animationDuration: `${GLEAM_CYCLE}s`,
                  animationDelay: `${GLEAM_AT[slot] - GLEAM_CYCLE}s`,
                }}
              >
                <circle cy="2" r="24" fill="url(#bulb-flare)" />
                <ellipse cy="2" rx="12" ry="13" fill="url(#bulb-lit)" />
                <ellipse
                  cx="-4.5"
                  cy="-3"
                  rx="4.2"
                  ry="7"
                  transform="rotate(-20 -4.5 -3)"
                  fill="url(#bulb-gleam)"
                />
              </g>
            )}
          </g>
        )
      })}
    </svg>
    {/* "SATURDAY" over the 12, built from the THURSDAY lettering's own
        glyphs so it's the same font, size and slant. */}
    <img
      src="/save-the-date-frames/saturday.svg"
      alt=""
      draggable={false}
      className="absolute inset-0 block h-full w-full select-none"
    />
    {/* "INVITATION TO FOLLOW" under the place, built from the card's own
        lettering (F is the card's E without its bottom bar). */}
    <img
      src="/save-the-date-frames/invitation.svg"
      alt=""
      draggable={false}
      className="absolute inset-0 block h-full w-full select-none"
    />
    {/* "June" under the 12, the same script as under the 10. */}
    <img
      src="/save-the-date-frames/june12.svg"
      alt=""
      draggable={false}
      className="absolute inset-0 block h-full w-full select-none"
    />
    {SPOTS.map((s) => {
      const photo = SAVE_THE_DATE.photos[s.photo]
      const mask = `url(/save-the-date-frames/${s.frame}.png)`
      return (
        <div
          key={s.frame}
          className={`absolute overflow-hidden ${
            !photo
              ? 'bg-[#C9D0D9]'
              : 'clear' in s && s.clear
              ? ''
              : 'bg-[#F2F1EB]'
          }`}
          style={{
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: `${s.w}%`,
            height: `${s.h}%`,
            WebkitMaskImage: mask,
            maskImage: mask,
            WebkitMaskSize: '100% 100%',
            maskSize: '100% 100%',
            transform: 'flip' in s && s.flip ? 'scaleX(-1)' : undefined,
          }}
        >
          {/* Cover the spot, anchored at the top. Below 1, zoom shrinks the
              photo toward the bottom of the spot, leaving a little space
              above heads so the rounded corners never clip them. */}
          {photo && (
            <img
              src={photo.src}
              alt=""
              draggable={false}
              className={`block h-full w-full select-none ${
                'contain' in s && s.contain
                  ? 'object-contain object-bottom'
                  : 'object-cover object-top'
              }`}
              style={{
                // (un-mirror the photo itself inside a mirrored spot)
                // (a mirrored spot also mirrors the turn, so undo both)
                transform: `${
                  'flip' in s && s.flip
                    ? `rotate(${-('turn' in s ? s.turn : 0)}deg) scaleX(-1) `
                    : `rotate(${'turn' in s ? s.turn : 0}deg) `
                }scale(${s.zoom})`,
                transformOrigin: s.focus,
              }}
            />
          )}
        </div>
      )
    })}
    {/* A heavier pass over the design's hand-drawn green border, following
        the same wobbly edge. On top, so photos never cover it. */}
    <img
      src="/save-the-date-frames/border.svg"
      alt=""
      draggable={false}
      className="absolute inset-0 block h-full w-full select-none"
    />
  </div>
)
